// @ts-ignore untyped module
import { useFeedbackLoader } from 'feedback-loader/useFeedbackLoader';

import { KEY_PREFIX } from 'marketplace-ui-common/hooks/useSearchParams';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'quartz-routing/react-router';
import { FilterOption } from '../../../__generated__/chirp/com/hubspot/marketplace/storefront/service/rpc/MarketplaceStorefrontRpc';
import { Paths } from '../../constants/paths';
import { useAppContext } from '../../context/AppContext';
import { useFilterConfig } from '../../filters/hooks/useFilterConfig';
import {
  INITIAL_POPULAR_COUNT,
  INITIAL_POPULAR_INDUSTRY_COUNT,
  POPULAR_INDUSTRY_SLUGS,
  POPULAR_SERVICE_SLUGS,
} from '../constants/find-partner-match';
import { useGetPartnerCount } from './useGetPartnerCount';

const QUIZ_COMPLETION_COUNT_KEY = 'mktpl_sp_quiz_completions';
const CSAT_DWELL_DELAY_MS = 30_000;

// Delays the CSAT survey on first quiz completion to avoid prompting the user
// immediately after landing on results. On subsequent completions the survey
// fires right away since the user has already had time to browse.
// Returns the timer handle on first visit so the caller can cancel it on unmount.
function loadCsatAfterGuard(
  loadSurveyFn: (id: number, type: string) => void
): ReturnType<typeof setTimeout> | undefined {
  let count = 0;
  try {
    count = parseInt(
      localStorage.getItem(QUIZ_COMPLETION_COUNT_KEY) || '0',
      10
    );
    localStorage.setItem(QUIZ_COMPLETION_COUNT_KEY, String(count + 1));
  } catch (_e) {
    // localStorage unavailable
  }

  if (count >= 1) {
    loadSurveyFn(1467, 'CSAT');
    return undefined;
  }
  return setTimeout(() => loadSurveyFn(1467, 'CSAT'), CSAT_DWELL_DELAY_MS);
}

export function useFindPartnerMatch(onClose: () => void) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [requireOfficeInCountry, setRequireOfficeInCountry] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [showMore, setShowMore] = useState(false);
  const csatTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (csatTimerRef.current !== null) clearTimeout(csatTimerRef.current);
    },
    []
  );
  const history = useHistory();
  const location = useLocation();
  const {
    PROFILE_CATALOG_SERVICES: serviceFilter,
    PROFILE_INDUSTRIES: industryFilter,
    PROFILE_LOCATION_COUNTRY: countryFilter,
  } = useFilterConfig({
    productTypes: ['SOLUTIONS_PARTNER_PROFILE'],
  });
  const { loadSurvey, clientLoaded } = useFeedbackLoader();
  const appContext = useAppContext();

  const toSelectOptions = (options: readonly FilterOption[]) =>
    options.map(({ icon: __icon, ...rest }) => rest);

  const serviceOptions = serviceFilter?.options
    ? toSelectOptions(serviceFilter.options)
    : [];

  const partnerCount = useGetPartnerCount(
    selectedServices,
    selectedIndustries,
    selectedCountry,
    requireOfficeInCountry,
    selectedLanguages
  );

  const allPopularServices = !serviceFilter?.options
    ? []
    : POPULAR_SERVICE_SLUGS.map((slug) =>
        serviceFilter.options.find((o) => o.slug === slug)
      ).filter(
        (option): option is FilterOption =>
          !!option && !selectedServices.includes(option.value)
      );

  const popularServices = showMore
    ? allPopularServices
    : allPopularServices.slice(0, INITIAL_POPULAR_COUNT);

  const industryOptions = industryFilter?.options
    ? toSelectOptions(industryFilter.options)
    : [];

  const popularIndustries: FilterOption[] = !industryFilter?.options
    ? []
    : POPULAR_INDUSTRY_SLUGS.map((slug) =>
        industryFilter.options.find((o) => o.slug === slug)
      )
        .filter(
          (option): option is FilterOption =>
            !!option && !selectedIndustries.includes(option.value)
        )
        .slice(0, INITIAL_POPULAR_INDUSTRY_COUNT);

  const countryOptions = countryFilter?.options
    ? toSelectOptions(countryFilter.options)
    : [];

  const navigateToResults = (
    industries: string[],
    country: string | null,
    languages: string[],
    requireOffice: boolean
  ) => {
    if (selectedServices.length === 0) return;
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete(`${KEY_PREFIX}PROFILE_CATALOG_SERVICES`);
    selectedServices.forEach((service) => {
      urlParams.append(`${KEY_PREFIX}PROFILE_CATALOG_SERVICES`, service);
    });
    urlParams.delete(`${KEY_PREFIX}PROFILE_INDUSTRIES`);
    industries.forEach((industry) => {
      urlParams.append(`${KEY_PREFIX}PROFILE_INDUSTRIES`, industry);
    });
    urlParams.delete(`${KEY_PREFIX}PROFILE_LOCATION_COUNTRY`);
    if (country) {
      urlParams.set(`${KEY_PREFIX}PROFILE_LOCATION_COUNTRY`, country);
    }
    urlParams.delete(`${KEY_PREFIX}PROFILE_OFFICE_LOCATION`);
    if (requireOffice && country) {
      urlParams.set(`${KEY_PREFIX}PROFILE_OFFICE_LOCATION`, country);
    }
    urlParams.delete(`${KEY_PREFIX}PROFILE_LANGUAGES`);
    languages.forEach((language) => {
      urlParams.append(`${KEY_PREFIX}PROFILE_LANGUAGES`, language);
    });
    history.push({
      pathname: Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS,
      search: `?${urlParams.toString()}`,
    });
    onClose();
  };

  const handleNext = () => {
    if (step === 1) {
      if (selectedServices.length === 0) return;
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    } else {
      navigateToResults(
        selectedIndustries,
        selectedCountry,
        selectedLanguages,
        requireOfficeInCountry
      );

      if (clientLoaded && appContext === 'app') {
        // locked-nav-item survey
        const handle = loadCsatAfterGuard(loadSurvey);
        if (handle !== undefined) csatTimerRef.current = handle;
      }
    }
  };

  const handleSkip = () => {
    if (step === 2) {
      setSelectedIndustries([]);
      setStep(3);
    } else if (step === 3) {
      setSelectedCountry(null);
      setRequireOfficeInCountry(false);
      setStep(4);
    } else if (step === 4) {
      navigateToResults(
        selectedIndustries,
        selectedCountry,
        [],
        requireOfficeInCountry
      );

      if (clientLoaded && appContext === 'app') {
        // locked-nav-item survey
        const handle = loadCsatAfterGuard(loadSurvey);
        if (handle !== undefined) csatTimerRef.current = handle;
      }
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    } else if (step === 4) {
      setStep(3);
    }
  };

  return {
    step,
    setStep,
    selectedServices,
    setSelectedServices,
    selectedIndustries,
    setSelectedIndustries,
    selectedCountry,
    setSelectedCountry,
    requireOfficeInCountry,
    setRequireOfficeInCountry,
    selectedLanguages,
    setSelectedLanguages,
    showMore,
    setShowMore,
    partnerCount,
    serviceOptions,
    allPopularServices,
    popularServices,
    industryOptions,
    popularIndustries,
    countryOptions,
    handleNext,
    handleSkip,
    handleBack,
  };
}
